import { useState, useEffect, useCallback, useMemo } from "react";
import { useSettingsStore } from "@/stores/settings";
import { useUiStore } from "@/stores/ui";
import { useMobileStore } from "@/stores/mobile";
import { useSearchPreferencesStore } from "@/stores/searchPreferences";
import { useInteractiveGenerationStore } from "@/stores/interactiveGenerationStore";
import { useAppStatusStore } from "@/stores/appStatus";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { useProjectsStore } from "@/stores/projects";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import {
  LogOut,
  Smartphone,
  Trash2,
  Power,
  Bot,
  ClipboardCopy,
  Sparkles,
  Github,
  Code2,
  Asterisk,
  Send,
  PlusCircle,
  Edit,
} from "lucide-react";
import { http } from "@/lib/http";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ProviderLimitsForm } from "@/components/ProviderLimitsForm";
import { Badge } from "@/components/ui/badge";

export default function SettingsPage() {
  const settings = useSettingsStore();
  console.log(settings)
  const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);
  const { disconnectDevice, logout } = useMobileStore();
  const {
    activeProjectId,
    byId: projectsById,
    updateProject: updateProjectAction,
  } = useProjectsStore();
  const activeProject = activeProjectId ? projectsById[activeProjectId] : null
  const { toolAvailability } = useAppStatusStore();
  const pairedDevices = useMobileStore((state) =>
    state.list.map((id) => state.byId[id])
  );

  const { searchMethod, setSearchMethod } = useSearchPreferencesStore();
  const { isInteractiveGeneration, setIsInteractiveGeneration } = useInteractiveGenerationStore();

  const isDesktopSession =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  const [isPairing, setIsPairing] = useState(false);
  const [pairingOtp, setPairingOtp] = useState<string | null>(null);
  const [isRestarting, setIsRestarting] = useState(false);

  // State for LLM Provider Management. Can be a provider object for editing, or a new object for adding.
  const [editingProvider, setEditingProvider] = useState<any | null>(null);

  // Local state for forms
  const [devConfig, setDevConfig] = useState(
    activeProject?.devSessionConfig || {}
  );
  const [projectSettings, setProjectSettings] = useState({
    proxyPort: activeProject?.proxyPort ?? "",
  });

  useEffect(() => {
    // Update local form state when the active project changes
    setDevConfig(
      activeProject?.devSessionConfig || {
        sessionName: "merged-dev",
        startup: [{}, {}],
      }
    );
    setProjectSettings({ proxyPort: activeProject?.proxyPort ?? "" });
  }, [activeProject]);

  const handleDevConfigChange = useCallback(
    (
      field:
        | "sessionName"
        | "be_command"
        | "be_dir"
        | "fe_command"
        | "fe_dir"
        | "windowsShell"
        | "precommand",
      value: string
    ) => {
      setDevConfig((currentConfig) => {
        const newConfig = JSON.parse(JSON.stringify(currentConfig || {}));

        if (!newConfig.startup) newConfig.startup = [{}, {}];
        while (newConfig.startup.length < 2) newConfig.startup.push({});
        if (!newConfig.startup[0]) newConfig.startup[0] = {};
        if (!newConfig.startup[1]) newConfig.startup[1] = {};

        newConfig.startup[0].name = "backend";
        newConfig.startup[1].name = "frontend";

        switch (field) {
          case "sessionName":
            newConfig.sessionName = value;
            break;
          case "windowsShell":
            newConfig.windowsShell = value;
            break;
          case "precommand":
            newConfig.precommand = value;
            break;
          case "be_command":
            newConfig.startup[0].command = value;
            break;
          case "be_dir":
            newConfig.startup[0].directory = value;
            break;
          case "fe_command":
            newConfig.startup[1].command = value;
            break;
          case "fe_dir":
            newConfig.startup[1].directory = value;
            break;
        }
        return newConfig;
      });
    },
    []
  );
  const handleSaveProjectSettings = () => {
    if (activeProjectId) {
      updateProjectAction(activeProjectId, {
        devSessionConfig: devConfig,
        proxyPort: Number(projectSettings.proxyPort) || undefined,
      });
    }
  };

  useEffect(() => {
    setHeaderTitle("Settings");
  }, [setHeaderTitle]);

  // Helper to update nested state directly in the store.
  // It constructs a patch for the top-level key that contains the change.
  const updateSetting = useCallback(
    (path: string, value: any) => {
      const keys = path.split(".");
      const rootKey = keys[0] as keyof typeof settings;
      const currentState = useSettingsStore.getState();

      // Deep clone the part of the state we are modifying to avoid direct mutation
      const newRootStateSlice = JSON.parse(
        JSON.stringify(currentState[rootKey] || {})
      );

      let current: any = newRootStateSlice;
      if (keys.length > 1) {
        // Navigate down to the second to last key
        for (let i = 1; i < keys.length - 1; i++) {
          if (current[keys[i]] === undefined) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;
        settings.patch({ [rootKey]: newRootStateSlice } as any);
      } else {
        // This is for updating a root key directly, e.g., 'llmProviders'
        settings.patch({ [rootKey]: value } as any);
      }
    },
    [settings]
  );

  const handleSaveProvider = (provider: any) => {
    const newProviders = { ...(settings.llmProviders || {}) };
    if (!provider.id) {
      // Create new provider
      const newId =
        (provider.name || "new-provider").toLowerCase().replace(/\s+/g, "-") +
        "-" +
        Date.now();
      newProviders[newId] = { ...provider, id: newId };
    } else {
      // Update existing provider
      newProviders[provider.id] = provider;
    }
    updateSetting("llmProviders", newProviders);
    setEditingProvider(null);
  };

  const handleDeleteProvider = (providerId: string) => {
    const newProviders = { ...(settings.llmProviders || {}) };
    delete newProviders[providerId];
    updateSetting("llmProviders", newProviders);
  };

  const handleInitiatePairing = async () => {
    setIsPairing(true);
    try {
      const { otp } = await http<{ otp: string }>(
        "/api/auth/initiate-pairing",
        {
          method: "POST",
          sendProjectId: false,
        }
      );
      setPairingOtp(otp);
    } catch (error: any) {
      toast.error("Could not start pairing", { description: error.message });
      setIsPairing(false);
    }
  };

  const handleRestartServer = async () => {
    setIsRestarting(true);
    try {
      await http("/api/app/restart", { method: "POST", sendProjectId: false });
      toast.success("Server is restarting...", {
        description:
          "The application will become unresponsive and should reload automatically in a few seconds.",
      });
      // After sending the command, poll for the server to be back up.
      setTimeout(() => {
        window.location.reload();
      }, 10000); // Wait 10 seconds before reloading
    } catch (error: any) {
      toast.error("Failed to send restart command", {
        description: error.message,
      });
      setIsRestarting(false);
    }
  };

  const handleShutdownServer = async () => {
    setIsRestarting(true);
    try {
      await http("/api/app/shutdown", { method: "POST", sendProjectId: false });
      toast.success("Server is shutting down...", {
        description:
          "The application will become unresponsive and should reload automatically in a few seconds.",
      });
      // After sending the command, poll for the server to be back up.
      setTimeout(() => {
        window.location.reload();
      }, 10000); // Wait 10 seconds before reloading
    } catch (error: any) {
      toast.error("Failed to send shutdown command", {
        description: error.message,
      });
      setIsRestarting(false);
    }
  };

  const handleReindexProject = async () => {
    if (!activeProjectId) {
      toast.error("No active project selected.");
      return;
    }
    toast.info("Re-indexing started...", {
      description: "This may take a few minutes for large projects.",
    });
    try {
      await http("/api/indexing/start", {
        method: "POST",
        body: JSON.stringify({ projectId: activeProjectId, reindex: true }),
      });
    } catch (error: any) {
      toast.error("Failed to start re-indexing.", {
        description: error.message,
      });
    }
  };

  return (
      <div className="max-w-9xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <header className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold hidden md:block">Settings</h1>
          <div>
            <Button variant="ghost" onClick={() => window.history.back()}>
              Back
            </Button>
          </div>
        </header>

        <AlertDialog open={isPairing} onOpenChange={setIsPairing}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Pair a Mobile Device</AlertDialogTitle>
              <AlertDialogDescription>
                Enter the following code on your mobile device to connect it to
                this session. This code will expire in 5 minutes.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-8 text-center">
              {pairingOtp ? (
                <p className="text-4xl font-mono tracking-widest font-bold">
                  {pairingOtp}
                </p>
              ) : (
                <p>Generating code...</p>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setPairingOtp(null)}>
                Close
              </AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {/* Validators Section */}
          <Card>
            <CardHeader>
              <CardTitle>Validators</CardTitle>
              <CardDescription>
                Configure lint, typecheck, and test behaviors.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
                <div className="space-y-0.5">
                  <Label
                    htmlFor="fast-iteration"
                    className="text-base font-medium"
                  >
                    Fast Iteration
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Skip full validation if syntax is correct.
                  </p>
                </div>
                <Switch
                  id="fast-iteration"
                  checked={settings.validators.fastIteration}
                  onCheckedChange={(checked) =>
                    updateSetting("validators.fastIteration", checked)
                  }
                />
              </div>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="lint-enabled"
                    checked={settings.validators.lint.enabled}
                    onCheckedChange={(checked) =>
                      updateSetting("validators.lint.enabled", checked)
                    }
                  />
                  <Label
                    htmlFor="lint-enabled"
                    className="text-base font-medium"
                  >
                    Lint
                  </Label>
                </div>
                {settings.validators.lint.enabled && (
                  <div className="pl-8 space-y-2">
                    <Label>Ruleset</Label>
                    <RadioGroup
                      value={settings.validators.lint.ruleset}
                      onValueChange={(value) =>
                        updateSetting("validators.lint.ruleset", value)
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="recommended"
                          id="lint-recommended"
                        />
                        <Label htmlFor="lint-recommended">Recommended</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="strict" id="lint-strict" />
                        <Label htmlFor="lint-strict">Strict</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="custom" id="lint-custom" />
                        <Label htmlFor="lint-custom">Custom</Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>
              {/* Typecheck */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="typecheck-enabled"
                    checked={settings.validators.typecheck.enabled}
                    onCheckedChange={(checked) =>
                      updateSetting("validators.typecheck.enabled", checked)
                    }
                  />
                  <Label
                    htmlFor="typecheck-enabled"
                    className="text-base font-medium"
                  >
                    Typecheck
                  </Label>
                </div>
              </div>
              {/* Tests */}
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="tests-enabled"
                    checked={settings.validators.tests.enabled}
                    onCheckedChange={(checked) =>
                      updateSetting("validators.tests.enabled", checked)
                    }
                  />
                  <Label
                    htmlFor="tests-enabled"
                    className="text-base font-medium"
                  >
                    Tests
                  </Label>
                </div>
                {settings.validators.tests.enabled && (
                  <div className="pl-8 space-y-2">
                    <Label>Mode</Label>
                    <RadioGroup
                      value={settings.validators.tests.mode}
                      onValueChange={(value) =>
                        updateSetting("validators.tests.mode", value)
                      }
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="impacted" id="tests-impacted" />
                        <Label htmlFor="tests-impacted">Impacted</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="all" id="tests-all" />
                        <Label htmlFor="tests-all">All</Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          {/* Indexer Section */}
          <Card>
            <CardHeader>
              <CardTitle>Indexer</CardTitle>
              <CardDescription>
                Control how your repository is indexed for search and context.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
                <div className="space-y-0.5">
                  <Label htmlFor="auto-index">Auto-index repository</Label>
                </div>
                <Switch
                  id="auto-index"
                  checked={settings.indexer.autoIndex}
                  onCheckedChange={(checked) =>
                    updateSetting("indexer.autoIndex", checked)
                  }
                />
              </div>
                            <div className="space-y-2">
                <Label>Symbol Search Method</Label>
                 <p className="text-xs text-muted-foreground">
                    Choose the backend engine for semantic symbol search.
                  </p>
                <RadioGroup
                  value={searchMethod}
                  onValueChange={(value) => setSearchMethod(value as any)}
                  className="pt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="default" id="search-default" />
                    <Label htmlFor="search-default">Default (ChromaDB)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="altsearch" id="search-altsearch" />
                    <Label htmlFor="search-altsearch">
                      ALTSEARCH (Hybrid SQLite)
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
                <div className="space-y-0.5">
                  <Label htmlFor="background-indexing">
                    Background indexing
                  </Label>
                </div>
                <Switch
                  id="background-indexing"
                  checked={settings.indexer.background}
                  onCheckedChange={(checked) =>
                    updateSetting("indexer.background", checked)
                  }
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3 shadow-sm">
                <div className="space-y-0.5">
                  <Label htmlFor="vector-db-enabled">
                    Enable Semantic Index
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Required for semantic search and auto context. May be slow on some systems.
                  </p>
                </div>
                <Switch
                  id="vector-db-enabled"
                  checked={settings.flags?.isVectorDbEnabled ?? true}
                  onCheckedChange={(checked) => {
                    const newFlags = { ...(settings.flags || {}), isVectorDbEnabled: checked };
                    updateSetting("flags", newFlags);
                  }
                  }
                />
              </div>
              {activeProject && (
                <div className="pt-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="w-full">
                        Force Re-index Project
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will delete the existing index and rebuild it from scratch. This can be slow for large projects.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleReindexProject}>
                          Yes, Re-index
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </CardContent>
          </Card>
          {/* Appearance Section */}
          <Card>
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>
                Personalize the look and feel of the application.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Theme</Label>
                <RadioGroup
                  value={settings.appearance.theme}
                  onValueChange={(value) =>
                    updateSetting("appearance.theme", value)
                  }
                  className="flex space-x-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="system" id="theme-system" />
                    <Label htmlFor="theme-system">System</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="light" id="theme-light" />
                    <Label htmlFor="theme-light">Light</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="dark" id="theme-dark" />
                    <Label htmlFor="theme-dark">Dark</Label>
                  </div>
                </RadioGroup>
              </div>
              <div className="space-y-2">
                <Label htmlFor="font-size-scale">
                  Font Size Scale (
                  {settings.appearance.fontSizeScale.toFixed(2)})
                </Label>
                <Slider
                  id="font-size-scale"
                  min={0.9}
                  max={1.3}
                  step={0.05}
                  value={[settings.appearance.fontSizeScale]}
                  onValueChange={(value) =>
                    updateSetting("appearance.fontSizeScale", value[0])
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Workspace Section */}
          <Card>
            <CardHeader>
              <CardTitle>Workspace</CardTitle>
              <CardDescription>
                Configure the primary interaction mode for AI assistance.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Label>Interaction Mode</Label>
              <RadioGroup
                value={
                  settings.workspace.interactionMode === "llm_provider" &&
                  settings.workspace.llmProviderId
                    ? `llm_provider:${settings.workspace.llmProviderId}`
                    : settings.workspace.interactionMode
                }
                onValueChange={(value) => {
                  if (value.startsWith("llm_provider:")) {
                    const providerId = value.split(":")[1];
                    updateSetting("workspace", {
                      ...settings.workspace,
                      interactionMode: "llm_provider",
                      llmProviderId: providerId,
                    });
                  } else {
                    updateSetting("workspace", {
                      ...settings.workspace,
                      interactionMode: value,
                      llmProviderId: undefined,
                    });
                  }
                }}
                className="mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="copy" id="mode-copy" />
                  <Label
                    htmlFor="mode-copy"
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <ClipboardCopy className="h-4 w-4 text-muted-foreground" />
                    <span>Copy Context</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="qwen"
                    id="mode-qwen"
                    disabled={!toolAvailability.qwen}
                  />
                  <Label
                    htmlFor="mode-qwen"
                    className={cn(
                      "flex items-center gap-2",
                      !toolAvailability.qwen
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    )}
                  >
                    <Bot className="h-4 w-4 text-muted-foreground" />
                    <span>Generate with Qwen</span>
                    {!toolAvailability.qwen && (
                      <Badge variant="outline" className="text-xs">
                        Not Installed
                      </Badge>
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="gemini"
                    id="mode-gemini"
                    disabled={!toolAvailability.gemini}
                  />
                  <Label
                    htmlFor="mode-gemini"
                    className={cn(
                      "flex items-center gap-2",
                      !toolAvailability.gemini
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    )}
                  >
                    <Sparkles className="h-4 w-4 text-muted-foreground" />
                    <span>Generate with Gemini</span>
                    {!toolAvailability.gemini && (
                      <Badge variant="outline" className="text-xs">
                        Not Installed
                      </Badge>
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="copilot"
                    id="mode-copilot"
                    disabled={!toolAvailability.copilot}
                  />
                  <Label
                    htmlFor="mode-copilot"
                    className={cn(
                      "flex items-center gap-2",
                      !toolAvailability.copilot
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    )}
                  >
                    <Github className="h-4 w-4 text-muted-foreground" />
                    <span>Generate with Copilot</span>
                    {!toolAvailability.copilot && (
                      <Badge variant="outline" className="text-xs">
                        Not Installed
                      </Badge>
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="codex"
                    id="mode-codex"
                    disabled={!toolAvailability.codex}
                  />
                  <Label
                    htmlFor="mode-codex"
                    className={cn(
                      "flex items-center gap-2",
                      !toolAvailability.codex
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    )}
                  >
                    <Code2 className="h-4 w-4 text-muted-foreground" />
                    <span>Generate with Codex</span>
                    {!toolAvailability.codex && (
                      <Badge variant="outline" className="text-xs">
                        Not Installed
                      </Badge>
                    )}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="claude"
                    id="mode-claude"
                    disabled={!toolAvailability.claude}
                  />
                  <Label
                    htmlFor="mode-claude"
                    className={cn(
                      "flex items-center gap-2",
                      !toolAvailability.claude
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    )}
                  >
                    <Asterisk className="h-4 w-4 text-muted-foreground" />
                    <span>Generate with Claude</span>
                    {!toolAvailability.claude && (
                      <Badge variant="outline" className="text-xs">
                        Not Installed
                      </Badge>
                    )}
                  </Label>
                </div>
                {Object.values(settings.llmProviders || {})
                  .filter((p: any) => p.enabled)
                  .map((p: any) => (
                    <div className="flex items-center space-x-2" key={p.id}>
                      <RadioGroupItem
                        value={`llm_provider:${p.id}`}
                        id={`mode-llm-${p.id}`}
                      />
                      <Label
                        htmlFor={`mode-llm-${p.id}`}
                        className="flex items-center gap-2 cursor-pointer"
                      >
                        <Send className="h-4 w-4 text-muted-foreground" />
                        <span>Generate with {p.name}</span>
                      </Label>
                    </div>
                  ))}
              </RadioGroup>
              <div className="flex items-center justify-between rounded-lg border p-3 shadow-sm mt-4">
                <div className="space-y-0.5">
                  <Label htmlFor="interactive-mode" className="text-base font-medium">
                    Interactive Generation
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Run generation providers in an interactive terminal.
                  </p>
                </div>
                <Switch id="interactive-mode" checked={isInteractiveGeneration} onCheckedChange={setIsInteractiveGeneration} />
              </div>
            </CardContent>
          </Card>

          {/* YOLO Mode Section */}
          <Card>
            <CardHeader>
              <CardTitle>YOLO Mode</CardTitle>
              <CardDescription>
                Select providers to run in parallel when YOLO mode is active (when Interaction Mode is "Copy Context"). Qwen is required for evaluation.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.values(
                useMemo(() => {
                  const providers = [];
                  const builtInProviders = ['qwen', 'gemini', 'codex', 'copilot', 'claude'];
                  for (const p of builtInProviders) {
                    if (toolAvailability[p]) {
                      providers.push({ id: p, name: p.charAt(0).toUpperCase() + p.slice(1), isCustom: false });
                    }
                  }
                  for (const p of Object.values(settings.llmProviders || {})) {
                    if (p.enabled) {
                      providers.push({ id: p.id, name: p.name, isCustom: true });
                    }
                  }
                  return providers;
                }, [toolAvailability, settings.llmProviders])
              ).map((p: any) => (
                <div key={p.id} className="flex items-center justify-between">
                  <Label htmlFor={`yolo-${p.id}`} className={cn(p.id === 'qwen' && "font-bold")}>
                    {p.name} {p.id === 'qwen' && '(Evaluator)'}
                  </Label>
                  <Switch
                    id={`yolo-${p.id}`}
                    checked={
                      p.id === 'qwen' || (settings.workspace.yolo?.enabledProviders?.[p.id] ?? true)
                    }
                    onCheckedChange={(checked) =>
                      updateSetting(`workspace.yolo.enabledProviders.${p.id}`, checked)
                    }
                    disabled={p.id === 'qwen'} />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Dev Session Config */}
          {activeProject && (
            <Card>
              <CardHeader>
                <CardTitle>Active Project Settings</CardTitle>
                <CardDescription>
                  Configure settings for the active project:{" "}
                  <span className="font-semibold">{activeProject.name}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="proxyPort">
                    Application Port (for Proxy)
                  </Label>
                  <Input
                    id="proxyPort"
                    type="number"
                    value={projectSettings.proxyPort}
                    onChange={(e) =>
                      setProjectSettings((s) => ({
                        ...s,
                        proxyPort: e.target.value,
                      }))
                    }
                    placeholder="e.g., 3000"
                  />
                  <p className="text-xs text-muted-foreground pt-1">
                    The local port your application runs on. A proxy will listen
                    on 8004 and forward here.
                  </p>
                </div>
                <Separator />
                <h3 className="text-base font-semibold pt-2">
                  Development Session
                </h3>
                <div>
                  <Label htmlFor="sessionName">Session Name</Label>
                  <Input
                    id="sessionName"
                    value={devConfig.sessionName || ""}
                    onChange={(e) =>
                      handleDevConfigChange("sessionName", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Windows Shell</Label>
                  <RadioGroup
                    value={devConfig.windowsShell || "cmd"}
                    onValueChange={(value) =>
                      handleDevConfigChange("windowsShell", value)
                    }
                    className="flex space-x-4 pt-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="cmd" id="shell-cmd" />
                      <Label htmlFor="shell-cmd">CMD</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="powershell"
                        id="shell-powershell"
                      />
                      <Label htmlFor="shell-powershell">PowerShell</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="winbash" id="shell-winbash" />
                      <Label htmlFor="shell-winbash">Bash</Label>
                    </div>
                  </RadioGroup>
                  <p className="text-xs text-muted-foreground pt-1">
                    Select the shell for terminals on Windows. Bash requires
                    `WINBASH_PATH` to be set on the server.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="precommand">Prerun Script</Label>
                  <Textarea
                    id="precommand"
                    placeholder="e.g., source .venv/bin/activate"
                    value={devConfig.precommand || ""}
                    onChange={(e) =>
                      handleDevConfigChange("precommand", e.target.value)
                    }
                    className="font-mono text-xs"
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground pt-1">
                    A script to run before any commands in a new terminal. Use
                    `$PROJECT_ROOT` as a placeholder.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">Backend</Label>
                  <Input
                    placeholder="e.g., python run_server_windows.py"
                    value={devConfig.startup?.[0]?.command || ""}
                    onChange={(e) =>
                      handleDevConfigChange("be_command", e.target.value)
                    }
                  />
                  <Input
                    placeholder="e.g., backend"
                    value={devConfig.startup?.[0]?.directory || ""}
                    onChange={(e) =>
                      handleDevConfigChange("be_dir", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">Frontend</Label>
                  <Input
                    placeholder="e.g., npm run dev"
                    value={devConfig.startup?.[1]?.command || ""}
                    onChange={(e) =>
                      handleDevConfigChange("fe_command", e.target.value)
                    }
                  />
                  <Input
                    placeholder="e.g., ."
                    value={devConfig.startup?.[1]?.directory || ""}
                    onChange={(e) =>
                      handleDevConfigChange("fe_dir", e.target.value)
                    }
                  />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleSaveProjectSettings}>
                    Save Project Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="md:col-span-2 lg:col-span-1 xl:col-span-1">
            <CardHeader>
              <CardTitle>Custom AI Providers</CardTitle>
              <CardDescription>
                Configure OpenAI-compatible models for generation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {Object.values(settings.llmProviders || {}).length > 0 && (
                <div className="space-y-2 mb-4">
                  {Object.values(settings.llmProviders || {}).map((p: any) => (
                    <div
                      key={p.id}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded-md"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "h-2 w-2 rounded-full",
                            p.enabled ? "bg-green-500" : "bg-gray-400"
                          )}
                        />
                        <div>
                          <p className="font-medium text-sm">{p.name}</p>
                          <p className="text-xs text-muted-foreground font-mono">
                            {p.model}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setEditingProvider(p)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the provider "
                                {p.name}".
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteProvider(p.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!editingProvider && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() =>
                    setEditingProvider({
                      name: "",
                      model: "",
                      baseUrl: "",
                      apiKeyEnvVar: "",
                      enabled: true,
                    })
                  }
                >
                  <PlusCircle className="mr-2 h-4 w-4" /> Add New Provider
                </Button>
              )}
              {editingProvider && (
                <ProviderLimitsForm
                  provider={editingProvider}
                  onSave={handleSaveProvider}
                  onCancel={() => setEditingProvider(null)}
                />
              )}
            </CardContent>
          </Card>

          {isDesktopSession && (
            <Card>
              <CardHeader>
                <CardTitle>Mobile Devices</CardTitle>
                <CardDescription>
                  Manage mobile devices paired with this workspace.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pairedDevices.length > 0 ? (
                  <ul className="space-y-3">
                    {pairedDevices.map((device) => (
                      <li
                        key={device.deviceId}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                      >
                        <div className="flex items-center gap-3">
                          <Smartphone className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{device.deviceName}</p>
                            <p className="text-xs text-muted-foreground">
                              Paired on{" "}
                              {new Date(device.pairedAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => disconnectDevice(device.deviceId)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                          <span className="sr-only">Disconnect device</span>
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground mb-4">
                    No mobile devices are currently paired.
                  </p>
                )}
                <Button onClick={handleInitiatePairing} className="mt-4">
                  Pair new mobile device
                </Button>
              </CardContent>
            </Card>
          )}

          {!isDesktopSession && (
            <Card>
              <CardHeader>
                <CardTitle>Session</CardTitle>
                <CardDescription>
                  Log out from your current mobile session.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button variant="destructive" onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log Out
                </Button>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Application Management</CardTitle>
              <CardDescription>
                Control the application server process.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={isRestarting}>
                    <Power className="mr-2 h-4 w-4" />
                    {isRestarting ? "Restarting..." : "Restart Server"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will restart the backend server process. You will be
                      temporarily disconnected.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleRestartServer}>
                      Yes, Restart
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <p className="text-sm text-muted-foreground mt-2">
                Use this if the server becomes unresponsive or to apply certain
                configuration changes.
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" disabled={isRestarting}>
                    <Power className="mr-2 h-4 w-4" />
                    {isRestarting ? "Shutting down..." : "Shutdown Server"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will shutdown the backend server process. You will be
                      temporarily disconnected.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleShutdownServer}>
                      Yes, Shutdown
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>
      </div>
  );
}
